// src/components/molecules/FilterHeaderActionButton/FilterHeaderActionButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Button, useTheme } from '@mui/material';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import {
  operationalElevation,
  operationalFontSizes,
  operationalRadii,
  operationalSizing,
  operationalSpacing
} from '../../../design-system/tokens';

const getToneStyles = (theme, tone) => {
  const tones = {
    clear: {
      borderColor: theme.palette.divider,
      color: theme.palette.text.secondary,
      '&:hover': {
        borderColor: theme.palette.error.main,
        color: theme.palette.error.main,
        backgroundColor: `${theme.palette.error.light}20`
      },
      '&.Mui-disabled': {
        borderColor: theme.palette.action.disabled,
        color: theme.palette.action.disabled
      }
    },
    primary: {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: operationalElevation.surface
      }
    },
    secondary: {
      borderColor: theme.palette.secondary.main,
      color: theme.palette.secondary.main,
      '&:hover': {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        boxShadow: operationalElevation.surface
      }
    }
  };

  return tones[tone] || tones.primary;
};

const FilterHeaderActionButton = ({
  tooltip,
  label,
  icon,
  tone = 'primary',
  onClick,
  disabled = false,
  className = '',
  sx,
  ...props
}) => {
  const theme = useTheme();

  return (
    <Tooltip title={tooltip}>
      <span>
        <Button
          size="small"
          variant="outlined"
          startIcon={icon}
          onClick={onClick}
          disabled={disabled}
          className={`filter-header-action-button ${className}`}
          sx={{
            height: operationalSizing.compact,
            px: operationalSpacing.lg,
            py: operationalSpacing.xs,
            fontSize: operationalFontSizes.sm,
            borderRadius: `${operationalRadii.control}px`,
            ...getToneStyles(theme, tone),
            ...sx
          }}
          {...props}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
};

FilterHeaderActionButton.propTypes = {
  tooltip: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  tone: PropTypes.oneOf(['clear', 'primary', 'secondary']),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  sx: PropTypes.object
};

export default FilterHeaderActionButton;
